'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { createClient } from '@/lib/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { toast } from 'sonner'

// H6 FIX: Email validation
function validateEmail(email: string): { valid: boolean; error?: string } {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' }
  }

  // Check for common typos in popular email domains
  const domain = email.split('@')[1]?.toLowerCase()
  const commonTypos: Record<string, string> = {
    'gmial.com': 'gmail.com',
    'gmai.com': 'gmail.com',
    'gamil.com': 'gmail.com',
    'gnail.com': 'gmail.com',
    'yahooo.com': 'yahoo.com',
    'yaho.com': 'yahoo.com',
    'hotmial.com': 'hotmail.com',
    'hotmai.com': 'hotmail.com',
    'outlok.com': 'outlook.com',
    'outloo.com': 'outlook.com',
  }

  if (domain && commonTypos[domain]) {
    const suggestion = email.split('@')[0] + '@' + commonTypos[domain]
    return { valid: false, error: `Did you mean ${suggestion}?` }
  }

  return { valid: true }
}

// H7 FIX: Password complexity validation
function validatePassword(password: string): string[] {
  const errors: string[] = []

  if (password.length < 12) {
    errors.push('At least 12 characters')
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('At least one uppercase letter')
  }
  if (!/[a-z]/.test(password)) {
    errors.push('At least one lowercase letter')
  }
  if (!/[0-9]/.test(password)) {
    errors.push('At least one number')
  }
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    errors.push('At least one special character (!@#$%^&*...)')
  }

  return errors
}

export default function SignupPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [passwordErrors, setPasswordErrors] = useState<string[]>([])
  const router = useRouter()
  const supabase = createClient()

  // Real-time password validation feedback
  const handlePasswordChange = (value: string) => {
    setPassword(value)
    if (value) {
      setPasswordErrors(validatePassword(value))
    } else {
      setPasswordErrors([])
    }
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()

    // H6 FIX: Validate email before submission
    const emailValidation = validateEmail(email)
    if (!emailValidation.valid) {
      toast.error(emailValidation.error)
      return
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match')
      return
    }

    // H7 FIX: Validate password complexity
    const passwordValidationErrors = validatePassword(password)
    if (passwordValidationErrors.length > 0) {
      toast.error(`Password must include: ${passwordValidationErrors.join(', ')}`)
      return
    }

    setLoading(true)

    // C1 FIX: Use NEXT_PUBLIC_SITE_URL for production redirect
    const siteUrl = process.env.NEXT_PUBLIC_SITE_URL || window.location.origin
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${siteUrl}/auth/callback`,
      },
    })

    if (error) {
      // M3 FIX: Generic error message to prevent user enumeration
      console.error('Signup error:', error.message)
      toast.error('Could not create account. Please try again or use a different email.')
      setLoading(false)
      return
    }

    toast.success('Check your email to confirm your account')
    router.push('/login')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Create an account</CardTitle>
          <CardDescription>
            Get started with C4 Owner
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSignup}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password <span className="text-red-500">*</span></Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                required
                minLength={12}
              />
              {/* H7 FIX: Real-time password strength feedback */}
              {password && passwordErrors.length > 0 && (
                <div className="text-xs space-y-1 mt-2">
                  <p className="text-muted-foreground">Password must include:</p>
                  {passwordErrors.map((err) => (
                    <p key={err} className="text-red-500">✗ {err}</p>
                  ))}
                </div>
              )}
              {password && passwordErrors.length === 0 && (
                <p className="text-xs text-green-600 mt-2">✓ Password meets all requirements</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password <span className="text-red-500">*</span></Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={12}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button
              type="submit"
              className="w-full"
              disabled={loading || passwordErrors.length > 0 || password !== confirmPassword}
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
            <p className="text-sm text-center text-muted-foreground">
              Already have an account?{' '}
              <Link href="/login" className="text-primary hover:underline">
                Sign in
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  )
}
